import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter, Event } from '@patsnap/tahiti-core/common/event';
import type {
    HistoryItem,
    HistoryState,
    GetHistoryListRequest,
    HistoryDetail,
    HistoryDetailTemp,
} from '../type/history.type';
import { HistoryModel } from '../model/history.model';
import {
    GetTaskIdDTO,
    GetHistoryListDTO,
    PutHistoryDTO,
    GetHistoryAsyncStatusListDTO,
    GetHistoryDetailDTO,
    PutHistoryReadDTO,
} from './history.repository.interface';
import { HISTORY_DEFAULT_PAGE, HISTORY_LIST_LIMIT } from '@/constants';

const LOOP_INTERVAL = 30000;

export const HistoryService = Symbol('HistoryService');

export interface HistoryService {
    getModelData: () => HistoryState;
    readonly onStatesChange: Event<HistoryState>;
    createTaskId: (type: string) => Promise<string>;
    updateTitle: (title: string) => void;
    putHistory: (params: { [key: string]: any }) => Promise<void>;
    putHistoryRead: (taskId: string) => void;
    getHistoryList: (params: GetHistoryListRequest) => void;
    refreshHistoryList: () => void;
    getNextPageHistory: () => void;
    getHistoryDetail: (taskId: string) => Promise<HistoryDetail>;
    toggleHistoryList: (showHistoryList: boolean) => void;
    updateTempData: (getUpdateTempData: (temp: HistoryDetailTemp) => HistoryDetailTemp) => void;
}

@injectable()
export class HistoryServiceImpl implements HistoryService {
    private _onStatesChangeEmitter = new Emitter<HistoryState>();
    readonly onStatesChange = this._onStatesChangeEmitter.event;

    constructor(
        @inject(HistoryModel)
        private _model: HistoryModel,
        @inject(GetTaskIdDTO)
        private _getTaskIdDTO: GetTaskIdDTO,
        @inject(GetHistoryListDTO)
        private _getHistoryListDTO: GetHistoryListDTO,
        @inject(PutHistoryDTO)
        private _putHistoryDTO: PutHistoryDTO<{ [key: string]: any }>,
        @inject(GetHistoryAsyncStatusListDTO)
        private _getHistoryAsyncStatusListDTO: GetHistoryAsyncStatusListDTO,
        @inject(GetHistoryDetailDTO)
        private _getHistoryDetailDTO: GetHistoryDetailDTO,
        @inject(PutHistoryReadDTO)
        private _putHistoryReadDTO: PutHistoryReadDTO,
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
    }

    private _emitModelDataChange() {
        this._onStatesChangeEmitter.fire(this.getModelData());
    }

    public getModelData(): HistoryState {
        return this._model.modelData;
    }

    private _setModelData(value: Partial<HistoryState>) {
        this._model.update(modelData => {
            return { ...modelData, ...value };
        });
    }

    public toggleHistoryList(showHistoryList: boolean) {
        this._setModelData({ showHistoryList });
    }

    public updateTempData(getUpdateTempData: (temp: HistoryDetailTemp) => HistoryDetailTemp) {
        const temp = this.getModelData().historyDetailTemp;

        this._setModelData({ historyDetailTemp: getUpdateTempData(temp) });
    }

    public async getHistoryList(params: GetHistoryListRequest) {
        const res = await this._getHistoryListDTO.fetch(params);

        this._setModelData({ historyList: res.list, page: params.page, total: res.total });
        this._getHistoryAsyncStatusLoop(res.list);
    }

    public refreshHistoryList() {
        this.getHistoryList({
            page: HISTORY_DEFAULT_PAGE,
            limit: HISTORY_LIST_LIMIT,
        });
    }

    private _timerId;

    private _getHistoryAsyncStatusLoop(historyList: HistoryItem[]) {
        if (this._timerId) {
            clearTimeout(this._timerId);
        }

        const needGetStatusTasks = historyList.filter(item => item.asyncStatus !== 'Complete' && item.taskType === 'ASYNC');
        const taskIds = needGetStatusTasks.map(item => item.taskId);
        if (taskIds.length === 0) return;

        this._timerId = setTimeout(async () => {
            const historyAsyncStatusList = await this._getHistoryAsyncStatusList(taskIds);
            if (historyAsyncStatusList.length > 0) {
                historyList = historyList.map(item => {
                    const asyncStatusItem = historyAsyncStatusList.find(asyncItem => asyncItem.taskId === item.taskId);
                    if (asyncStatusItem) {
                        return asyncStatusItem;
                    }
                    return item;
                });

                this._setModelData({ historyList });
            }
            this._getHistoryAsyncStatusLoop(historyList);
        }, LOOP_INTERVAL);
    }

    public async getNextPageHistory() {
        const { page, historyList, total } = this.getModelData();

        if (historyList.length >= total) return;

        const nextPage = page + 1;
        const res = await this._getHistoryListDTO.fetch({
            page: nextPage, limit: HISTORY_LIST_LIMIT,
        });
        const newHistoryList = [...historyList, ...res.list];

        this._getHistoryAsyncStatusLoop(newHistoryList);


        this._setModelData({ page: nextPage, historyList: newHistoryList });
    }

    public updateTitle(title: string) {
        const temp = this.getModelData().historyDetailTemp;
        this._setModelData({ historyDetailTemp: { ...temp, title } });
        this.putHistory({ title });
    }

    public async putHistory(params: { [key: string]: any }) {
        const temp = this.getModelData().historyDetailTemp;

        await this._putHistoryDTO.fetch({ task_id: temp.taskId, ...params });
    }

    private async _getHistoryAsyncStatusList(taskIds: string[]) {
        return await this._getHistoryAsyncStatusListDTO.fetch(taskIds);
    }

    public async getHistoryDetail(taskId: string) {
        const res = await this._getHistoryDetailDTO.fetch(taskId);
        this._setModelData({ historyDetailTemp: { title: res.title || '', taskId } });
        return res;
    }

    public putHistoryRead(taskId: string) {
        this._putHistoryReadDTO.fetch(taskId);
    }

    public async createTaskId(type: string) {
        const taskId = await this._getTaskIdDTO.fetch(type);
        const temp = this.getModelData().historyDetailTemp;

        this._setModelData({ taskId, historyDetailTemp: { ...temp, taskId } });
        return taskId;
    }
}